import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';

// #681: the token-consume flow extracted as a pure async helper so the
// handoff sequence (exchange token → establish impersonation → navigate,
// plus the error path) is unit-testable without a DOM/effects harness.
// Returns { ok } on success or { ok: false, error } on failure.
export async function consumeHandoffToken(token, { startImpersonation, navigate }) {
  try {
    const result = await api.get('/auth/handoff', { token });
    // vista#670: `{ data, cursor, error }`. An envelope with no payload is a
    // failure, not a handoff with an undefined token — starting impersonation
    // with `undefined` would look like a successful handoff and then 401 on
    // the next call, which is a much harder thing to diagnose.
    const data = result?.data;
    if (!data?.impersonation_token) {
      return { ok: false, error: 'Handoff response carried no token' };
    }

    // Set impersonation token and fetch /me — does NOT re-init session
    await startImpersonation(data.impersonation_token);

    navigate('/', { replace: true });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.body?.error || err.message || 'Handoff failed' };
  }
}

export default function Handoff() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [error, setError] = useState(null);
  const [consumed, setConsumed] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token || consumed) return;
    setConsumed(true);
    consumeHandoffToken(token, { startImpersonation, navigate }).then((res) => {
      if (!res.ok) setError(res.error);
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-gray-900">Handoff Failed</h1>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <p className="mt-4 text-xs text-gray-400">Return to the Admin Console and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <p className="mt-4 text-sm text-gray-500">Opening organization view...</p>
      </div>
    </div>
  );
}
